import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
  signal,
  computed,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, merge, finalize } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  OrderTimelineEvent,
} from '../../../../../core/services/order-timeline/order-timeline-event.model';
import { OrderTimelineService } from '../../../../../core/services/order-timeline/order-timeline.service';
import { ModalComponent } from '../../../../../shared/components/modal/modal.component';
import { OrderFullTimelineComponent } from '../order-full-timeline/order-full-timeline.component';

@Component({
  selector: 'app-order-timeline',
  standalone: true,
  imports: [CommonModule, ModalComponent, OrderFullTimelineComponent],
  templateUrl: './order-timeline.component.html',
})
export class OrderTimelineComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) orderId!: string;
  @Input() occurredEventsInput: OrderTimelineEvent[] = [];
  @Input() expectedEventsInput: OrderTimelineEvent[] = [];
  @Input() timelineLoading = false;

  @ViewChild('eventModal') eventModal!: ModalComponent;
  @ViewChild('fullTimelineModal') fullTimelineModal!: ModalComponent;

  private readonly orderTimelineService = inject(OrderTimelineService);
  private readonly destroy$ = new Subject<void>();
  private readonly cancelPrefetch$ = new Subject<void>();

  loading = signal(false);
  fullTimelineLoading = signal(false);
  fullTimelineData = signal<unknown>(null);
  fullTimelineError = signal<string | null>(null);

  occurredEvents = signal<OrderTimelineEvent[]>([]);
  expectedEvents = signal<OrderTimelineEvent[]>([]);

  ngOnInit(): void {
    this.updateEvents();
    this.prefetchFullTimeline();
  }

  ngOnDestroy(): void {
    this.cancelPrefetch$.next();
    this.cancelPrefetch$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['occurredEventsInput'] || changes['expectedEventsInput']) {
      this.updateEvents();
    }

    const orderIdChange = changes['orderId'];
    if (orderIdChange) {
      const newValue = orderIdChange.currentValue;
      const prevValue = orderIdChange.previousValue;

      if (newValue && !orderIdChange.firstChange && newValue !== prevValue) {
        this.prefetchFullTimeline();
      }
    }
  }

  private updateEvents(): void {
    this.occurredEvents.set(this.occurredEventsInput || []);
    this.expectedEvents.set(this.expectedEventsInput || []);
  }

  private fetchFullTimeline(): void {
    this.cancelPrefetch$.next();
    this.fullTimelineData.set(null);
    this.fullTimelineError.set(null);
    this.fullTimelineLoading.set(true);

    this.orderTimelineService
      .getFullTimeline(this.orderId)
      .pipe(
        takeUntil(merge(this.cancelPrefetch$, this.destroy$)),
        finalize(() => this.fullTimelineLoading.set(false))
      )
      .subscribe({
        next: (data) => this.fullTimelineData.set(data),
        error: (err) => {
          const msg =
            err?.error?.message ||
            err?.message ||
            'Não foi possível carregar os detalhes da linha de pedido.';
          this.fullTimelineError.set(msg);
        },
      });
  }

  private prefetchFullTimeline(): void {
    if (!this.orderId) return;
    this.fetchFullTimeline();
  }

  // 🔹 Timeline unificada + flags de destaque
  timelineItems = computed<TimelineItemView[]>(() => {
    const occurred = this.occurredEvents();
    const expected = this.expectedEvents();

    const completed: TimelineItemView[] = occurred.map((item, index) => {
      let systemOrigin = item.fromSystemName as SYSTEM_NAME;
      if(systemOrigin === SYSTEM_NAME.TUNA || systemOrigin === SYSTEM_NAME.VTEX || systemOrigin === SYSTEM_NAME.CONECTALA || systemOrigin === SYSTEM_NAME.ANYMARKET ){
        systemOrigin = `hook ${systemOrigin}` as any;
      }

      const isLast = index === occurred.length - 1;

      return {
        name: item.name,
        systemOrigin: systemOrigin,
        systemTarget: item.toSystemName as SYSTEM_NAME,
        transferredAt: item.date,
        status: isLast ? "completed-last" : "completed",
        statusDescription: item.description
      };
    });

    let pendingCurrentIndex: number | null = null;
    const pending: TimelineItemView[] = expected.map((item, index) => {
      const isKruzer = item.fromSystemName === SYSTEM_NAME.KRUZER;
      let systemOrigin = item.fromSystemName as SYSTEM_NAME;

      if(systemOrigin === SYSTEM_NAME.TUNA || systemOrigin === SYSTEM_NAME.VTEX || systemOrigin === SYSTEM_NAME.CONECTALA ){
        systemOrigin = `hook ${systemOrigin}` as any;
      }

      let status: TimelineStatusView = "pending-future";

      if(index === 0){
        status = isKruzer ? "pending-error" : "pending-current";
        pendingCurrentIndex = index;
      }

      const lastPendingItem = expected[pendingCurrentIndex as number];

      if(lastPendingItem?.description === "pagamento aprovado / pronto para manuseio"){
        if (item.description === "pagamento aprovado / pronto para manuseio") status = "pending-current"

        const lastNextPendingItem = expected?.[(pendingCurrentIndex as number) + 1];



const nextNextItem = expected?.[(pendingCurrentIndex as number) + 2];

const isKruzerNextNext = isKruzer && nextNextItem?.fromSystemName === item.fromSystemName && nextNextItem?.toSystemName === item.toSystemName && nextNextItem?.description === item.description && nextNextItem?.name === item.name && nextNextItem?.date === item.date

        if(isKruzerNextNext && lastNextPendingItem?.description === "pagamento aprovado / pronto para manuseio"){
          status = "pending-error"
        }
      }

      const isAfterCurrent = index === ((pendingCurrentIndex as any) + 1);
      const containsPendingIndex = Number.isInteger(pendingCurrentIndex);

      if(containsPendingIndex && isAfterCurrent && isKruzer) {
        status = "pending-error";
      }

      return {
        name: item.name,
        systemOrigin: systemOrigin,
        systemTarget: item.toSystemName as SYSTEM_NAME,
        status,
        transferredAt: null,
        statusDescription: item.description
      } as TimelineItemView;
    });

    const items: TimelineItemView[] = [...completed, ...pending];

    return items.map((item) => ({
      ...item,
      systemTarget: item.systemTarget === SYSTEM_NAME.KRUZER ? 'Krz' : item.systemTarget,
      systemOrigin: item.systemOrigin === SYSTEM_NAME.KRUZER ? 'Krz' : item.systemOrigin
    } as any));
  });

  formatDate(date: string): string {
    return new Date(date).toLocaleString('pt-BR');
  }

  statusLabel(status?: string | null): string {
    if (!status) return '--';
    return status.replace(/-/g, ' ');
  }

  openFullTimeline(): void {
    if (!this.orderId) return;

    // Path 1: data already available
    if (this.fullTimelineData()) {
      this.fullTimelineModal.openDialog();
      return;
    }

    // Path 2: prefetch in progress — just open modal (loading state shows automatically)
    if (this.fullTimelineLoading()) {
      this.fullTimelineModal.openDialog();
      return;
    }

    // Path 3: no data, not loading — retry (transparent to user)
    this.fetchFullTimeline();
    this.fullTimelineModal.openDialog();
  }
}



export enum SYSTEM_NAME {
  VTEX = 'Vtex',
  KRUZER = 'Kruzer',
  GAN = 'Gan',
  TUNA = 'Tuna',
  CONECTALA = 'Conectala',
  ANYMARKET = 'Anymarket'
}

export enum EVENT_NAME {
  ORDER_CREATED = 'OrderCreated',
  PAYMENT_PENDING = 'PaymentPending',
  PAYMENT_APPROVED = 'PaymentApproved',
  ORDER_INVOICED = 'OrderInvoiced'
}

type TimelineStatusView =
  | 'completed'
  | 'completed-last'
  | 'pending-current'
  | 'pending-future'
  | 'pending-error'

type TimelineItemView = {
  name: string;
  systemOrigin: SYSTEM_NAME;
  systemTarget: SYSTEM_NAME;
  transferredAt: string | null;
  status: TimelineStatusView;
  statusDescription: string;
}
